import { useState } from "react";
import { itemScopeApplies, qualifyItemScope, qualifySegmentScope, type GeneratedDefinition } from "@keydials/api-client";
import { selectAllItemsIn, toggleItem } from "./fieldOps";

/** Every word the tree shows, passed in by the app that owns the copy. */
export interface ItemScopeTreeLabels {
  /** The tree's heading: "Which items?". */
  heading: string;
  /** Each action point's button: "Select all". */
  selectAll: string;
  /** That button's full name, for one action point: "Select every item in Extinguishers". */
  selectAllIn: (name: string) => string;
  /** How many items the field is on: "2 of 5 items". */
  count: (ticked: number, total: number) => string;
  /** Why the last ticked item can't be unticked. */
  lastItem: string;
  /** The name of a section, action point or item that has none yet. */
  untitled: string;
}

export interface ItemScopeTreeProps {
  model: GeneratedDefinition;
  /** The item field whose items are being chosen. */
  fieldKey: string;
  /** The field's scope as it stands: the `section/segment/item` paths it's on (legacy bare item keys read as the server reads them). */
  scope: string[] | null | undefined;
  labels: ItemScopeTreeLabels;
  disabled?: boolean;
  /** Applies a change to the dial, as the builder's autosaved edits do. */
  onEdit: (change: (model: GeneratedDefinition) => GeneratedDefinition) => void;
}

/**
 * The items an item field that's only on some items is on (KEY-560, Phase 3): every section and action point with items, always
 * fully open, a checkbox for each item and a way to select all of one action point's. Ticks go through the shared field edits,
 * which keep the server's rule that the field stays on at least one item, and the tree says so when that's why a tick stayed.
 */
export function ItemScopeTree({ model, fieldKey, scope, labels, disabled = false, onEdit }: ItemScopeTreeProps) {
  const [refused, setRefused] = useState(false);

  const sections = model.sections
    .map((section) => ({ ...section, segments: section.segments.filter((segment) => segment.items.length > 0) }))
    .filter((section) => section.segments.length > 0);
  const applies = (sectionKey: string, segmentKey: string, itemKey: string) => itemScopeApplies(scope, sectionKey, segmentKey, itemKey);
  const items = sections.flatMap((s) => s.segments.flatMap((g) => g.items.map((i) => applies(s.key, g.key, i.key))));
  const ticked = items.filter(Boolean).length;

  function toggle(path: string, checked: boolean) {
    // The shared edit refuses to untick the last item, since no ticks would put the field on every item.
    if (checked && toggleItem(model, fieldKey, path) === model) {
      setRefused(true);
      return;
    }
    setRefused(false);
    onEdit((m) => toggleItem(m, fieldKey, path));
  }

  return (
    <div className="kd-scope-tree" role="group" aria-label={labels.heading}>
      <h4 className="kd-panel-heading">{labels.heading}</h4>
      {sections.map((section) => (
        <div key={section.key} className="kd-scope-section">
          <div className="kd-scope-section-name">{section.label || labels.untitled}</div>
          {section.segments.map((segment) => {
            const name = segment.name || labels.untitled;
            const allTicked = segment.items.every((item) => applies(section.key, segment.key, item.key));
            return (
              <div key={segment.key} className="kd-scope-segment">
                <div className="kd-scope-segment-head">
                  <span className="kd-scope-segment-name">{name}</span>
                  <button
                    type="button"
                    className="kd-builder-link"
                    aria-label={labels.selectAllIn(name)}
                    disabled={disabled || allTicked}
                    onClick={() => {
                      setRefused(false);
                      onEdit((m) => selectAllItemsIn(m, fieldKey, qualifySegmentScope(section.key, segment.key)));
                    }}
                  >
                    {labels.selectAll}
                  </button>
                </div>
                <ul className="kd-scope-items">
                  {segment.items.map((item) => {
                    const checked = applies(section.key, segment.key, item.key);
                    return (
                      <li key={item.key}>
                        <label className="kd-scope-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggle(qualifyItemScope(section.key, segment.key, item.key), checked)}
                          />
                          {item.name || labels.untitled}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      ))}
      {refused && (
        <p className="kd-builder-error" role="alert">
          {labels.lastItem}
        </p>
      )}
      <p className="kd-builder-muted">{labels.count(ticked, items.length)}</p>
    </div>
  );
}
